import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatAgo, formatShortTime, ChatTemperature, leadTemperature } from '../../chat.format';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatPresenceStatus, ChatUser, isStaffRow } from '../../chat.model';
import { ChatIconName } from '../chat-icon/chat-icon.component';

/** Os mesmos ícones do quadro de negócios (funnel-card.vm.ts, INTEREST_ICON). */
const TEMPERATURE_ICON: Record<ChatTemperature, string> = {
  hot: 'ri-fire-line',
  warm: 'ri-sun-line',
  cold: 'mdi mdi-snowflake'
};

/**
 * One row of the conversation list: avatar with presence, name, short
 * relative time of the last message ("agora", "5 min", "Ontem", "Seg",
 * "12/09"), preview with a type icon for attachments, unread badge (pulses
 * once when it grows) and the property reference. A single <button>, so the
 * whole row is one target and one tab stop; the active row gets a primary
 * bar on the left.
 *
 * CRM: a supervised conversation shows the owner's name as a tag; a colleague
 * (any staff role) shows "Equipa". In the "Por atribuir" tab (`queue`)
 * the time slot shows "há X min" from `waitingSince` instead of the clock.
 */
@Component({
  selector: 'chat-conversation-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-conversation-item.component.html',
  styleUrls: ['./chat-conversation-item.component.css']
})
export class ChatConversationItemComponent implements OnChanges {
  @Input() conversation!: ChatUser;
  @Input() presence: ChatPresenceStatus | null = null;
  @Input() active = false;
  /** Row of the "Por atribuir" tab: the time is the waiting time. */
  @Input() queue = false;
  /**
   * Row of the "Supervisão" view (2026-10-26): it sits under its consultant's
   * block, so no owner tag; the preview says who wrote last ("Ana: …") and a
   * client waiting for an answer gets "à espera há X".
   */
  @Input() supervision = false;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() select = new EventEmitter<ChatUser>();

  time = '';
  preview = '';
  previewIcon: ChatIconName | null = null;
  /** v3: temperatura da lead (quente / morno / frio), a partir do score. */
  temperature: ChatTemperature | null = null;
  /** O ícone do quadro de negócios (2026-10-27: era uma bolinha sem legenda) e a dica. */
  temperatureIcon = '';
  temperatureTip = '';
  unread = 0;
  tag = '';
  /** "à espera há 12 min" (Supervisão). */
  waitingText = '';
  /** The badge just grew: plays the pulse once (reset on animationend). */
  pulse = false;

  private lastUnread = 0;

  /** "Quente · score 82" (sem score: só "Quente"). */
  private temperatureText(score?: number | null): string {
    const label = this.temperature === 'hot' ? this.strings.temperatureHot
      : this.temperature === 'warm' ? this.strings.temperatureWarm : this.strings.temperatureCold;
    return score !== null && score !== undefined && !isNaN(Number(score))
      ? this.strings.temperatureTip.replace('{label}', label).replace('{score}', String(Math.round(Number(score))))
      : label;
  }

  ngOnChanges(): void {
    const c = this.conversation;
    if (this.queue && c && c.waitingSince) {
      this.time = formatAgo(c.waitingSince, this.strings);
    } else {
      this.time = c && c.time ? formatShortTime(c.time, this.strings) : '';
    }
    this.unread = c && c.unreadCount > 0 ? c.unreadCount : 0;
    this.temperature = c && c.lead ? leadTemperature(c.lead.category) : null;
    this.temperatureIcon = this.temperature ? TEMPERATURE_ICON[this.temperature] : '';
    this.temperatureTip = this.temperature ? this.temperatureText(c.lead ? c.lead.score : null) : '';
    if (this.unread > this.lastUnread) {
      this.pulse = true;
    }
    this.lastUnread = this.unread;
    const kindPreview = c && c.lastMessageKind ? this.strings.previewByKind[c.lastMessageKind] : '';
    if (c && c.lastMessageDeleted) {
      // Secção 9: a última mensagem foi apagada para todos.
      this.preview = this.strings.messageDeletedPreview;
      this.previewIcon = 'ban';
    } else if (c && c.message) {
      this.preview = c.lastMessageKind === 'note' && kindPreview ? kindPreview + ': ' + c.message : c.message;
      this.previewIcon = null;
    } else if (kindPreview) {
      // 7.2: uma visita, ficha, proposta... sem texto: o nome do cartão.
      this.preview = kindPreview;
      this.previewIcon = c.lastMessageKind === 'property' ? 'home'
        : c.lastMessageKind === 'visit' || c.lastMessageKind === 'visit_request' ? 'clock'
        : c.lastMessageKind === 'system' ? 'info' : 'file';
    } else if (c && c.lastMessageAudio) {
      // Mensagem de voz.
      this.preview = this.strings.voicePreview;
      this.previewIcon = null;
    } else if (c && (c.chat || (c.filesNumber && c.filesNumber > 0))) {
      // A conversation whose last message had no text: an attachment.
      const n = c.filesNumber || 0;
      this.preview = n > 1 ? this.strings.attachmentsPreview.replace('{n}', String(n)) : this.strings.attachmentPreview;
      this.previewIcon = 'clip';
    } else {
      this.preview = '';
      this.previewIcon = null;
    }
    if (this.supervision) {
      // Who wrote last, by first name: "Ana: tem garagem?" / "Maria: obrigado".
      const sender = c && c.lastSenderName ? String(c.lastSenderName).trim().split(/\s+/)[0] : '';
      if (sender && this.preview && c.lastMessageKind !== 'system' && !c.lastMessageDeleted) {
        this.preview = sender + ': ' + this.preview;
      }
      this.tag = '';
      this.waitingText = c && c.waitingSince && !this.queue ? this.strings.waitingTag.replace('{time}', formatAgo(c.waitingSince, this.strings)) : '';
    } else if (c && c.supervised) {
      this.tag = c.ownerName || '';
      this.waitingText = '';
    } else if (c && isStaffRow(c)) {
      this.tag = this.strings.teamTag;
      this.waitingText = '';
    } else {
      this.tag = '';
      this.waitingText = '';
    }
  }

  get badgeText(): string {
    return this.unread > 99 ? '99+' : String(this.unread);
  }

  onPulseEnd(): void {
    this.pulse = false;
  }
}
